import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Car, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, statusOf } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader, NoAccess, PageHeader, StatusPill } from "@/components/common";
import { Confirm } from "@/components/kids/kidsUi";
import { useLiveRefresh } from "@/lib/live";
import { writesLocked } from "@/lib/splits";
import { todayIn } from "@/lib/format";
import { Thumb } from "@/components/inventory/util";
import VehicleDialog from "@/components/vehicles/VehicleDialog";
import { OdometerDialog, ScheduleDialog, ServiceDialog } from "@/components/vehicles/Dialogs";
import { Docs, Profile, Recalls, Schedules, Services, dueText } from "@/components/vehicles/Parts";

const RGB = "var(--glow-home)";
const STATE_RGB = { OK: "var(--glow-money)", "Due soon": "var(--glow-lists)", Overdue: "239,68,68" };

function Detail({ base, id, hub, locked, onBack }) {
  const [v, setV] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get(`${base}/${id}`).then(({ data }) => setV(data)).catch((e) => toast.error(errMsg(e))), [base, id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "vehicles", 30000);
  if (!v) return <Loader />;
  const done = (data) => { setDlg(null); if (data?.id) setV(data); else load(); };
  const del = () => api.delete(`${base}/${id}/services/${dlg.s.id}`).then(({ data }) => { toast.success("Service deleted. Its Spending entry is gone too."); done(data); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="min-w-0 space-y-8" data-testid="vehicle-detail">
      <div className="flex flex-wrap items-center gap-3">
        <button className="q-btn q-btn-ghost min-h-[44px]" onClick={onBack} data-testid="vehicle-back"><ArrowLeft className="h-4 w-4" /> All vehicles</button>
        <h2 className="min-w-0 flex-1 truncate font-display text-2xl font-extrabold text-q-text" data-testid="vehicle-title">{v.nickname}</h2>
        <button className="q-btn q-btn-secondary min-h-[44px]" disabled={locked} onClick={() => setDlg({ k: "edit" })} data-testid="vehicle-edit-btn"><Pencil className="h-4 w-4" /> Edit</button>
      </div>
      {v.photo_id && <Thumb url={`${base}/${v.id}/photo`} alt={v.nickname} className="h-40 w-full max-w-md" testid="vehicle-photo-img" />}
      <Profile v={v} locked={locked} onOdo={() => setDlg({ k: "odo" })} />
      <section><h3 className="mb-2 font-display text-lg font-extrabold text-q-text">Maintenance</h3><Schedules v={v} locked={locked} onEdit={(s) => setDlg({ k: "sched", s })} /></section>
      <Services v={v} locked={locked} onAdd={() => setDlg({ k: "svc" })} onEdit={(s) => setDlg({ k: "svc", s })} onDelete={(s) => setDlg({ k: "del", s })} />
      <Recalls base={base} v={v} locked={locked} onData={setV} />
      <Docs v={v} />
      {dlg?.k === "edit" && <VehicleDialog base={base} v={v} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.k === "odo" && <OdometerDialog base={base} v={v} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.k === "sched" && <ScheduleDialog base={base} v={v} s={dlg.s} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.k === "svc" && <ServiceDialog base={base} v={v} s={dlg.s} today={todayIn(hub.timezone)} onClose={() => setDlg(null)} onSaved={done} />}
      <Confirm open={dlg?.k === "del"} onClose={() => setDlg(null)} title="Delete this service?" body="Its Car spending entry is removed too. The receipt stays in Documents." onConfirm={del} testid="service-delete-confirm" />
    </div>
  );
}

function Card({ base, v, onOpen }) {
  const oil = v.schedules.find((s) => s.key === "oil");
  const worst = v.schedules.find((s) => s.state === "Overdue") || v.schedules.find((s) => s.state === "Due soon");
  return (
    <button className="q-card q-rise flex min-h-[44px] min-w-0 items-center gap-3 p-3 text-left" onClick={onOpen} data-testid={`vehicle-card-${v.id}`}>
      {v.photo_id ? <Thumb url={`${base}/${v.id}/photo`} alt={v.nickname} className="h-16 w-20" /> : <span className="flex h-16 w-20 items-center justify-center rounded-xl bg-q-raised"><Car className="h-6 w-6 text-q-muted" /></span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold text-q-text">{v.nickname}</span>
        <span className="block truncate text-xs text-q-muted">{[v.year, v.make, v.model].filter(Boolean).join(" ")} · {Number(v.odometer).toLocaleString("en-US")} mi</span>
        {oil && <span className="block truncate text-xs text-q-muted" data-testid={`vehicle-oil-${v.id}`}>Oil change: {dueText(oil)}</span>}
      </span>
      {worst && <StatusPill label={worst.state} rgb={STATE_RGB[worst.state]} testid={`vehicle-state-${v.id}`} />}
      {v.recalls.count > 0 && <StatusPill label={`${v.recalls.count} recalls`} rgb="239,68,68" testid={`vehicle-recalls-${v.id}`} />}
    </button>
  );
}

export default function Vehicles() {
  const { hub } = useHub();
  const base = `/organizations/${hub.id}/vehicles`;
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const [add, setAdd] = useState(false);
  const locked = writesLocked(hub);
  const load = useCallback(() => api.get(base).then(({ data }) => setD(data)).catch((e) => (statusOf(e) === 403 ? setDenied(true) : toast.error(errMsg(e)))), [base]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "vehicles", 30000);
  if (denied) return <NoAccess />;
  if (!d) return <Loader />;
  const open = sp.get("v");
  return (
    <div className="min-w-0" data-testid="vehicles-page">
      <PageHeader eyebrow="Home" title="Vehicles" rgb={RGB} testid="vehicles-title" />
      {open ? <Detail key={open} base={base} id={open} hub={hub} locked={locked} onBack={() => { setSp({}); load(); }} /> : (
        <>
          <button className="q-btn q-btn-primary mb-4 min-h-[44px]" disabled={locked} onClick={() => setAdd(true)} data-testid="vehicle-add-btn"><Plus className="h-4 w-4" /> Add vehicle</button>
          {!d.vehicles.length ? <EmptyState title="No vehicles yet" hint="Add a car with its VIN and we'll track oil changes, renewals and recalls." rgb={RGB} testid="vehicles-empty" /> : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="vehicles-list">{d.vehicles.map((v) => <Card key={v.id} base={base} v={v} onOpen={() => setSp({ v: v.id })} />)}</div>
          )}
        </>
      )}
      {add && <VehicleDialog base={base} onClose={() => setAdd(false)} onSaved={(v) => { setAdd(false); load(); setSp({ v: v.id }); }} />}
    </div>
  );
}
